import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { selectors as authSelectors } from '@hixme/auth-ui'
import { selectors as userSessionSelectors } from 'store/modules/user-session'

import { isDemoHrAdminRestricted } from 'enrollment-platform/demo-restriction'
import { consumeFreshLogin, clearFreshLogin } from './fresh-login'
import { postDemoEmployerLogin } from './demo-event.api'

/**
 * Fires the `employer_login` EPS demo event exactly once per real login for
 * a demo company's HR admin.
 *
 * Conditions that must all be true before the event is sent:
 * - The current session started from a real username/password or MFA login
 *   (not a page reload or silent token refresh).
 * - The Redux role and company have resolved (company.id is non-empty).
 * - The user is a demo company HR admin (`isDemoHrAdminRestricted`).
 *
 * When any of those conditions is false the fresh-login flag is cleared so
 * a later company switch cannot accidentally send the event.
 *
 * Place this hook inside AdminLayout so it runs once the authenticated shell
 * mounts, alongside `useDemoRouteGuard`.
 */
export function useDemoLoginEvent() {
  const role = useSelector(authSelectors.getRole)
  const company = useSelector(userSessionSelectors.getCurrentCompanyNew)
  const companyId = company?.id
  const restricted = isDemoHrAdminRestricted(role, company)

  useEffect(() => {
    // Wait until both role and company have loaded.
    if (!role || !companyId) return

    if (restricted && consumeFreshLogin()) {
      postDemoEmployerLogin(companyId)
    } else {
      // Not a demo HR admin (or already consumed) — discard any pending flag
      // so it doesn't linger across company switches.
      clearFreshLogin()
    }
  }, [role, companyId, restricted])
}
